import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import LashingForm from "@/components/LashingForm";
import LabelingForm from "@/components/LabelingForm";
import DamagedShipmentForm from "@/components/DamagedShipmentForm";
import TransloadingForm from "@/components/TransloadingForm";
import PhotoUploader from "@/components/PhotoUploader";
import TemplatePicker from "@/components/TemplatePicker";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FloppyDisk, CheckCircle } from "@phosphor-icons/react";

const EMPTY_LASHING = {
  report_type: "lashing", applicant: "", reference_number: "", date_of_issuance: "", surveyor: "",
  date_of_inspection: "", place_of_inspection: "", shipper: "", notify_party: "",
  port_of_loading: "", place_of_delivery: "", commodity_summary: "", total_gross_weight: "",
  containers: [],
  survey_findings: "Acting upon the instructions of the Applicant, the undersigned surveyor attended at the place of inspection to witness and report on the loading, securing and sealing of the cargo into the container(s).",
  observations: "",
  disclaimer: "This report has been prepared for the exclusive use of the client. The surveyor accepts no liability for any consequences arising from the use of this report by any third party.",
  photos: [],
};

const EMPTY_LABELING = {
  report_type: "labeling", applicant: "", reference_number: "", date_of_issuance: "", surveyor: "",
  container_id: "", rail_car_number: "", cargo: "", service: "Labeling",
  site_of_service: "", location: "", date_of_nomination: "", date_of_service: "", completion_time: "",
  removal_table: [], affixing_table: [], remarks: "", photos: [],
};

const EMPTY_LABEL_REMOVAL = { ...EMPTY_LABELING, report_type: "label_removal", service: "Removal" };

const EMPTY_DAMAGED_SHIPMENT = {
  report_type: "damaged_shipment", applicant: "", reference_number: "", customer_reference_number: "",
  date_of_issuance: "", date_of_inspection: "", place_of_inspection: "", surveyor: "",
  shipper: "", consignee: "", port_of_loading: "", place_of_delivery: "",
  carrier: "", vessel_details: "", container_number: "", seal_number: "",
  bill_of_lading_number: "", commodity_description: "", total_gross_weight: "", number_of_packages: "",
  date_damage_noticed: "", location_damage_noticed: "", damage_type: "", damage_severity: "",
  description_of_damage: "", suspected_cause: "", immediate_action_taken: "",
  customer_notified: false, claim_reference_number: "",
  survey_findings: "", observations: "", recommendation: "",
  disclaimer: "This report has been prepared for the exclusive use of the client. The report records the apparent condition of the cargo and/or packaging at the time and place of inspection. No liability is accepted for consequences arising from use of this report by any third party.",
  photo_entries: [], photos: [],
};

const EMPTY_TRANSLOADING = {
  report_type: "transloading", applicant: "", reference_number: "", customer_reference_number: "",
  date_of_issuance: "", date_of_operation: "", place_of_operation: "", surveyor: "",
  shipper: "", consignee: "", port_of_loading: "", place_of_delivery: "",
  cargo_description: "", total_gross_weight: "", number_of_packages: "",
  origin_container: "", destination_container: "", origin_seal: "", new_seal: "",
  equipment_used: "", warehouse_number: "",
  checklist: {}, operation_timeline: [],
  survey_findings: "Acting upon the instructions of the Applicant, the undersigned attended the place of operation to witness and report on the transloading of the cargo.",
  observations: "", exceptions: "", recommendation: "",
  disclaimer: "This report has been prepared for the exclusive use of the client. The report records the apparent condition of the cargo and handling operation at the time and place stated. No liability is accepted for consequences arising from use of this report by any third party.",
  photo_entries: [], photos: [],
};

const DEFAULTS = {
  lashing: EMPTY_LASHING, labeling: EMPTY_LABELING, label_removal: EMPTY_LABEL_REMOVAL,
  damaged_shipment: EMPTY_DAMAGED_SHIPMENT, transloading: EMPTY_TRANSLOADING,
};

const TYPE_LABELS = {
  lashing: "Cargo Securing Survey Report", labeling: "Labeling Report",
  label_removal: "Label Removal Report", damaged_shipment: "Damaged Shipment Report",
  transloading: "Transloading Photo Report",
};

export default function ReportFormPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!id;
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (id) {
      axios.get(`${API}/reports/${id}`)
        .then(({ data }) => { setFormData(data); setLoading(false); })
        .catch(() => { toast.error("Failed to load report"); navigate("/"); });
    } else {
      setFormData({ ...(DEFAULTS[type] || DEFAULTS.lashing) });
    }
  }, [id, type, navigate]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = async (finalize = false) => {
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const { data } = await axios.put(`${API}/reports/${id}`, formData);
        saved = data;
      } else {
        const { data } = await axios.post(`${API}/reports`, formData);
        saved = data;
      }
      if (finalize) {
        await axios.post(`${API}/reports/${saved.id}/finalize`, {});
        toast.success("Report finalized successfully");
      } else {
        toast.success(isEdit ? "Report updated" : "Report saved as draft");
      }
      navigate(`/reports/${saved.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save report");
    } finally { setSaving(false); }
  };

  const handlePhotoUploaded = useCallback((photoId) => {
    setFormData(prev => ({ ...prev, photos: [...(prev.photos || []), photoId] }));
  }, []);
  const handleRemovePhoto = useCallback((photoId) => {
    setFormData(prev => ({ ...prev, photos: (prev.photos || []).filter(p => p !== photoId) }));
  }, []);
  const handleReorderPhotos = useCallback((reordered) => {
    setFormData(prev => ({ ...prev, photos: reordered }));
  }, []);

  if (loading || !formData) {
    return (<div className="min-h-screen bg-slate-50"><Header /><div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" /></div></div>);
  }

  const reportType = formData.report_type || type;
  const isLegacyType = ["lashing", "labeling", "label_removal"].includes(reportType);
  const templateCat = reportType === "lashing" ? "disclaimer" : "remark";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="report-form-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft size={18} /></Button>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {isEdit ? `Edit ${formData.report_number || "Report"}` : `New ${TYPE_LABELS[reportType]}`}
              </h1>
              {formData.report_number && <p className="text-sm text-slate-500">{formData.report_number}</p>}
            </div>
          </div>
          <div className="flex gap-2" data-testid="form-actions">
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="gap-2" data-testid="save-draft-btn">
              <FloppyDisk size={16} /> {saving ? "Saving..." : "Save Draft"}
            </Button>
            <Button onClick={() => handleSave(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="finalize-btn">
              <CheckCircle size={16} weight="fill" /> Finalize
            </Button>
          </div>
        </div>

        <div className="space-y-6 fade-in">
          {reportType === "lashing" && <LashingForm data={formData} onChange={updateField} />}
          {(reportType === "labeling" || reportType === "label_removal") && <LabelingForm data={formData} onChange={updateField} isRemoval={reportType === "label_removal"} />}
          {reportType === "damaged_shipment" && <DamagedShipmentForm data={formData} onChange={updateField} />}
          {reportType === "transloading" && <TransloadingForm data={formData} onChange={updateField} />}

          {isLegacyType && (
            <>
              <TemplatePicker category={templateCat} currentValue={reportType === "lashing" ? formData.disclaimer : formData.remarks} onInsert={(text) => updateField(reportType === "lashing" ? "disclaimer" : "remarks", text)} />
              <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="photo-section">
                <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>General Photos</h3>
                <PhotoUploader photos={formData.photos || []} onPhotoUploaded={handlePhotoUploaded} onRemovePhoto={handleRemovePhoto} onReorder={handleReorderPhotos} />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pb-8" data-testid="bottom-actions">
            <Button variant="outline" onClick={() => navigate("/")} data-testid="cancel-btn">Cancel</Button>
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="gap-2" data-testid="save-draft-bottom-btn"><FloppyDisk size={16} /> Save Draft</Button>
            <Button onClick={() => handleSave(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="finalize-bottom-btn"><CheckCircle size={16} weight="fill" /> Finalize Report</Button>
          </div>
        </div>
      </main>
    </div>
  );
}
